import { useCallback, useEffect, useRef, useState } from 'react'
import { connect } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useHistory, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import Btn from '../../../../UI/Btn'
import BtnAdd from '../../../../UI/BtnAdd'
import CompanyProgress from '../../../../UI/CompanyProgress'
import FilePreview from '../../../../UI/FilePreview'
import Loading from '../../../../UI/Loading'
import MiniHeader from '../../../../UI/MiniHeader'

import http from '../../../../shared/http'
import { TApi, TRoutes } from '../../../../shared/const'
import { askConfirm } from '../../../../shared/confirmDialog'
import {
	JMC_CLASS_ROLE_LABELS,
	JMC_GENDER_LABELS,
	JMC_GRADE_LABELS,
	JMC_STATUS,
	optionsOf
} from '../../../../shared/miniCompanies'
import {
	CONSENT_TEMPLATE,
	DetailsList,
	SchoolDetails,
	DownloadsList,
	LogList,
	StatusPill,
	TeacherList,
	downloadPdf,
	formatDay,
	personName
} from '../../common/MiniCompany/parts'

import styles from '../../common/MiniCompany/page.module.scss'

const MAX_BYTES = 10 * 1024 * 1024
const neuesKind = { gender: '', firstName: '', lastName: '' }

/**
 * Eine Junior Mini Company aus Sicht der Lehrkraft (Anforderung #92) — der
 * ganze Ablauf auf einer Seite, in der Reihenfolge der Anforderung:
 * Angaben → Kinder → Unterlagen herunterladen → Einverständniserklärungen
 * hochladen → einreichen; danach Zertifikate. Daneben Lehrkräfte und Verlauf.
 *
 * Ändern geht nur im Entwurf oder nach einer Rückgabe (`editable` kommt vom
 * Server). Jede Handlung antwortet mit dem neuen Stand der ganzen JMC.
 */
function TeacherMiniCompany({ ownId }) {
	const { t } = useTranslation()
	const history = useHistory()
	const { id } = useParams()
	const [jmc, setJmc] = useState(null)
	const [log, setLog] = useState([])
	const [fehler, setFehler] = useState('')
	const [busy, setBusy] = useState('')
	const [details, setDetails] = useState(null)
	const [kind, setKind] = useState(null)
	const [kindEdit, setKindEdit] = useState(null)
	const [email, setEmail] = useState('')
	const [vorschau, setVorschau] = useState(false)
	const fileInput = useRef(null)

	const ladeVerlauf = useCallback(() => {
		http.get(TApi.TEACHER_MINI_COMPANY_LOG(id), { disableToast: true })
			.then(({ data }) => setLog(data.items || []))
			.catch(() => setLog([]))
	}, [id])

	useEffect(() => {
		http.get(TApi.TEACHER_MINI_COMPANY(id), { disableToast: true })
			.then(({ data }) => setJmc(data))
			.catch(() => setFehler(t('The Junior Mini Company could not be loaded.')))
		ladeVerlauf()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [id, ladeVerlauf])

	/** Handlung ausführen, neuen Stand übernehmen, Verlauf nachladen. */
	const handle = (name, request, success) => {
		setBusy(name)
		return request()
			.then(({ data }) => {
				if (data && data.id) setJmc(data)
				ladeVerlauf()
				if (success) toast.success(success)
				return data
			})
			.finally(() => setBusy(''))
	}

	if (fehler) return <div className={styles.root}><p className={styles.notice}>{fehler}</p></div>
	if (!jmc) return <Loading />

	const editable = jmc.editable
	const setFeld = (setter, key) => event => {
		const value = event.target.value
		setter(prev => ({ ...prev, [key]: value }))
	}

	/* ------------------------------------------------------------- Angaben */
	const speichereAngaben = event => {
		event.preventDefault()
		const { name, businessDescription, grade } = details
		if (![name, businessDescription, grade].every(v => String(v).trim())) return
		handle('details', () => http.put(TApi.TEACHER_MINI_COMPANY(id), { name, businessDescription, grade }), t('Saved')).then(
			() => setDetails(null)
		)
	}

	const eigeneRolle = event => {
		const classRole = event.target.value
		if (!classRole) return
		handle('role', () => http.put(TApi.TEACHER_MINI_COMPANY_CLASS_ROLE(id), { classRole }), t('Saved'))
	}

	/* ---------------------------------------------------------------- Kinder */
	const kindGueltig = k => k && k.gender && k.firstName.trim() && k.lastName.trim()

	const kindAnlegen = event => {
		event.preventDefault()
		if (!kindGueltig(kind)) return
		handle('kind', () => http.post(TApi.TEACHER_MINI_COMPANY_PARTICIPANTS(id), kind)).then(() => setKind({ ...neuesKind }))
	}

	const kindSpeichern = event => {
		event.preventDefault()
		if (!kindGueltig(kindEdit)) return
		const { id: participantId, gender, firstName, lastName } = kindEdit
		handle('kind', () =>
			http.put(TApi.TEACHER_MINI_COMPANY_PARTICIPANT(id, participantId), { gender, firstName, lastName })
		).then(() => setKindEdit(null))
	}

	const kindEntfernen = async p => {
		const ok = await askConfirm({
			title: t('Remove child'),
			text: t('Remove {{name}} from the list?', { name: personName(p) }),
			confirmLabel: t('Remove'),
			tone: 'danger'
		})
		if (ok) handle('kind', () => http.delete(TApi.TEACHER_MINI_COMPANY_PARTICIPANT(id, p.id)))
	}

	/* ------------------------------------------------ Einverständniserklärung */
	const hochladen = event => {
		const file = event.target.files && event.target.files[0]
		event.target.value = ''
		if (!file) return
		if (file.type !== 'application/pdf') return toast.error(t('Only PDF files are allowed.'))
		if (file.size > MAX_BYTES) return toast.error(t('The file is larger than 10 MB.'))
		const form = new FormData()
		form.append('consent', file)
		handle('consent', () => http.put(TApi.TEACHER_MINI_COMPANY_CONSENT(id), form), t('Consent forms uploaded'))
	}

	/* ------------------------------------------------------------ Einreichen */
	const einreichen = async () => {
		const ok = await askConfirm({
			title: t('Submit the Junior Mini Company to your state administration?'),
			text: t(
				'While it is being checked, nothing can be changed. Once it is approved, the details and the list of children are fixed.'
			),
			confirmLabel: t('Submit')
		})
		if (ok) handle('submit', () => http.post(TApi.TEACHER_MINI_COMPANY_SUBMIT(id)), t('Submitted for approval'))
	}

	const loeschen = async () => {
		const ok = await askConfirm({
			title: t('Delete Junior Mini Company'),
			text: t('Delete „{{name}}“? The children and the uploaded file are deleted too.', { name: jmc.name }),
			confirmLabel: t('Delete'),
			tone: 'danger'
		})
		if (ok) http.delete(TApi.TEACHER_MINI_COMPANY(id)).then(() => history.push(TRoutes.COMPANIES))
	}

	/* ------------------------------------------------------------ Lehrkräfte */
	const lehrkraftHinzufuegen = event => {
		event.preventDefault()
		const adresse = email.trim()
		if (!adresse) return
		handle('teacher', () => http.post(TApi.TEACHER_MINI_COMPANY_TEACHERS(id), { email: adresse })).then(() => setEmail(''))
	}

	const lehrkraftAustragen = async teacher => {
		const selbst = teacher.id === ownId
		const ok = await askConfirm({
			title: selbst ? t('Leave Junior Mini Company') : t('Remove teacher'),
			text: selbst
				? t('Leave this Junior Mini Company? You will no longer see it.')
				: t('Remove {{name}} from this Junior Mini Company?', { name: personName(teacher) }),
			confirmLabel: selbst ? t('Leave Junior Mini Company') : t('Remove'),
			tone: 'danger'
		})
		if (!ok) return
		if (selbst)
			return http
				.delete(TApi.TEACHER_MINI_COMPANY_TEACHER(id, teacher.id))
				.then(() => history.push(TRoutes.COMPANIES))
		handle('teacher', () => http.delete(TApi.TEACHER_MINI_COMPANY_TEACHER(id, teacher.id)))
	}

	const einladungZurueck = invitation =>
		handle('teacher', () => http.delete(TApi.TEACHER_MINI_COMPANY_INVITATION(id, invitation.id)))

	/* ----------------------------------------------------------- Zertifikate */
	const zertifikateDatei = `Zertifikate ${jmc.name}.pdf`
	const zertifikateErzeugen = async () => {
		const ok = await askConfirm({
			title: t('Generate the certificates now?'),
			text: t(
				'The list of children can no longer be changed, so the certificates are generated once. Your state administration receives a short notice.'
			),
			confirmLabel: t('Generate')
		})
		if (!ok) return
		handle('certificates', () => http.post(TApi.TEACHER_MINI_COMPANY_CERTIFICATES(id)), t('Certificates generated')).then(
			() => downloadPdf(TApi.TEACHER_MINI_COMPANY_CERTIFICATES(id), zertifikateDatei)
		)
	}

	const aktiv = jmc.status === JMC_STATUS.ACTIVE
	const kinder = jmc.participants || []

	return (
		<div className={styles.root}>
			<MiniHeader className={styles.miniHeader} title={jmc.name} bg="#f8faff">
				<div className={styles.headActions}>
					<StatusPill status={jmc.status} />
					<Btn theme="secondary" onClick={() => history.push(TRoutes.COMPANIES)}>
						{t('Back to the list')}
					</Btn>
				</div>
			</MiniHeader>

			<StandBanner jmc={jmc} />

			<CompanyProgress miniCompany={jmc} className={styles.progress} />

			<div className={styles.columns}>
				<div className={styles.main}>
					{/* 1. Angaben */}
					<section className={styles.card}>
						<div className={styles.cardHead}>
							<h2 className={styles.cardTitle}>{t('1. Details')}</h2>
							{editable && !details && (
								<Btn
									theme="secondary"
									size="small"
									onClick={() =>
										setDetails({ name: jmc.name, businessDescription: jmc.businessDescription, grade: jmc.grade })
									}
								>
									{t('Edit details')}
								</Btn>
							)}
						</div>
						{!details && <DetailsList miniCompany={jmc} />}
						{details && (
							<form onSubmit={speichereAngaben} noValidate>
								<label className={styles.field}>
									{t('Name of the Junior Mini Company')}
									<input type="text" value={details.name} maxLength={120} onChange={setFeld(setDetails, 'name')} />
								</label>
								<label className={styles.field}>
									{t('Description of the business activity')}
									<textarea
										value={details.businessDescription}
										maxLength={5000}
										onChange={setFeld(setDetails, 'businessDescription')}
									/>
								</label>
								<label className={styles.field}>
									{t('Year group of the pupils')}
									<select value={details.grade} onChange={setFeld(setDetails, 'grade')}>
										{optionsOf(JMC_GRADE_LABELS, t).map(o => (
											<option key={o.value} value={o.value}>
												{o.label}
											</option>
										))}
									</select>
								</label>
								<div className={styles.actions}>
									<Btn type="button" theme="secondary" onClick={() => setDetails(null)}>
										{t('Cancel')}
									</Btn>
									<Btn type="submit" loading={busy === 'details'}>
										{t('Save')}
									</Btn>
								</div>
							</form>
						)}
					</section>

					<section className={styles.card}>
						<h2 className={styles.cardTitle}>{t('School')}</h2>
						<SchoolDetails school={jmc.school} state={jmc.state} />
					</section>

					{/* 2. Kinder */}
					<section className={styles.card}>
						<div className={styles.cardHead}>
							<h2 className={styles.cardTitle}>
								{t('2. Children')} <span className={styles.count}>{kinder.length}</span>
							</h2>
						</div>
						{!kinder.length && (
							<p className={styles.muted}>{t('No child entered yet. Add each participating child with the + button.')}</p>
						)}
						{kinder.length > 0 && (
							<table className={styles.table}>
								<thead>
									<tr>
										<th>#</th>
										<th>{t('First name')}</th>
										<th>{t('Last name')}</th>
										<th>{t('Gender')}</th>
										{editable && <th />}
									</tr>
								</thead>
								<tbody>
									{kinder.map((p, index) =>
										kindEdit && kindEdit.id === p.id ? (
											<tr key={p.id}>
												<td>{index + 1}</td>
												<td>
													<input
														aria-label={t('First name')}
														value={kindEdit.firstName}
														maxLength={80}
														onChange={setFeld(setKindEdit, 'firstName')}
													/>
												</td>
												<td>
													<input
														aria-label={t('Last name')}
														value={kindEdit.lastName}
														maxLength={80}
														onChange={setFeld(setKindEdit, 'lastName')}
													/>
												</td>
												<td>
													<GenderSelect value={kindEdit.gender} onChange={setFeld(setKindEdit, 'gender')} />
												</td>
												<td className={styles.rowActions}>
													<Btn size="small" onClick={kindSpeichern} disabled={!kindGueltig(kindEdit)}>
														{t('Save')}
													</Btn>
													<Btn size="small" theme="secondary" onClick={() => setKindEdit(null)}>
														{t('Cancel')}
													</Btn>
												</td>
											</tr>
										) : (
											<tr key={p.id}>
												<td>{index + 1}</td>
												<td className={styles.strong}>{p.firstName}</td>
												<td className={styles.strong}>{p.lastName}</td>
												<td>{t(JMC_GENDER_LABELS[p.gender] || p.gender)}</td>
												{editable && (
													<td className={styles.rowActions}>
														<Btn size="small" theme="minimal" onClick={() => setKindEdit({ ...p })}>
															{t('Edit')}
														</Btn>
														<Btn size="small" theme="minimal" onClick={() => kindEntfernen(p)}>
															{t('Remove')}
														</Btn>
													</td>
												)}
											</tr>
										)
									)}
								</tbody>
							</table>
						)}
						{editable && !kind && (
							<div className={styles.addRow}>
								<BtnAdd type="button" onClick={() => setKind({ ...neuesKind })} />
								<span>{t('Add child')}</span>
							</div>
						)}
						{editable && kind && (
							<form className={styles.childForm} onSubmit={kindAnlegen} noValidate>
								<label className={styles.field}>
									{t('Gender')}
									<GenderSelect value={kind.gender} onChange={setFeld(setKind, 'gender')} />
								</label>
								<label className={styles.field}>
									{t('First name')}
									<input value={kind.firstName} maxLength={80} onChange={setFeld(setKind, 'firstName')} autoFocus />
								</label>
								<label className={styles.field}>
									{t('Last name')}
									<input value={kind.lastName} maxLength={80} onChange={setFeld(setKind, 'lastName')} />
								</label>
								<div className={styles.childActions}>
									<Btn type="submit" loading={busy === 'kind'} disabled={!kindGueltig(kind)}>
										{t('Add child')}
									</Btn>
									<Btn type="button" theme="secondary" onClick={() => setKind(null)}>
										{t('Cancel')}
									</Btn>
								</div>
							</form>
						)}
					</section>

					{/* 3. Unterlagen */}
					<section className={styles.card}>
						<h2 className={styles.cardTitle}>{t('3. Downloads')}</h2>
						<DownloadsList />
					</section>

					{/* 4. Einverständniserklärungen */}
					<section className={styles.card}>
						<h2 className={styles.cardTitle}>{t('4. Consent forms')}</h2>
						<p className={styles.hint}>
							{t('All consent forms together as one PDF file (max. 10 MB). A new upload replaces the previous file.')}
						</p>
						{/* Die Vorlage steht hier noch einmal, direkt beim Hochladen (Peter, 31.10.2026). */}
						<p className={styles.hint}>
							{t('Use the template for this:')}{' '}
							<a className={styles.inlineLink} href={CONSENT_TEMPLATE.file} target="_blank" rel="noopener noreferrer" download>
								{t('Download consent form template')}
							</a>
						</p>
						{jmc.consent ? (
							<p className={styles.file}>
								<button type="button" className={styles.fileLink} onClick={() => setVorschau(true)}>
									{jmc.consent.originalName}
								</button>
								<span>{t('Uploaded on {{date}}', { date: formatDay(jmc.consent.uploadedAt) })}</span>
							</p>
						) : (
							<p className={styles.muted}>{t('No file uploaded yet.')}</p>
						)}
						{editable && (
							<>
								<input ref={fileInput} type="file" accept="application/pdf" hidden onChange={hochladen} data-testid="jmc-consent-input" />
								<Btn theme="secondary" loading={busy === 'consent'} onClick={() => fileInput.current && fileInput.current.click()}>
									{jmc.consent ? t('Replace PDF') : t('Upload PDF')}
								</Btn>
							</>
						)}
					</section>

					{/* 5. Einreichen */}
					<section className={styles.card}>
						<h2 className={styles.cardTitle}>{t('5. Submit')}</h2>
						{editable ? (
							<>
								{jmc.missingForSubmit?.length ? (
									<div className={styles.missing}>
										<strong>{t('Still missing:')}</strong>
										<ul>
											{jmc.missingForSubmit.map(m => (
												<li key={m}>{m}</li>
											))}
										</ul>
									</div>
								) : (
									<p className={styles.hint}>
										{t('Ready to submit. The state administration checks the Junior Mini Company and activates it.')}
									</p>
								)}
								<div className={styles.actions}>
									<Btn theme="secondary" onClick={loeschen}>
										{t('Delete Junior Mini Company')}
									</Btn>
									<Btn onClick={einreichen} loading={busy === 'submit'} disabled={!!jmc.missingForSubmit?.length}>
										{t('Submit for approval')}
									</Btn>
								</div>
							</>
						) : (
							<p className={styles.hint}>
								{jmc.status === JMC_STATUS.SUBMITTED
									? t(
											'Submitted on {{date}}. The state administration is checking the Junior Mini Company — until then nothing can be changed.',
											{ date: formatDay(jmc.submittedAt) }
									  )
									: t('Since the approval, the details and the list of children can no longer be changed.')}
							</p>
						)}
					</section>

					{/* Zertifikate */}
					<section className={styles.card}>
						<h2 className={styles.cardTitle}>{t('JMC certificates')}</h2>
						{!aktiv && (
							<p className={styles.muted}>
								{t('Certificates are available once the state administration has approved the Junior Mini Company.')}
							</p>
						)}
						{aktiv && (
							<>
								<p className={styles.hint}>
									{t(
										'One page per child with first and last name, the Junior Mini Company, the school, the place of the school and today’s date. Your state administration is informed.'
									)}
								</p>
								{jmc.certificatesGeneratedAt && (
									<p className={styles.hint}>{t('Generated on {{date}}.', { date: formatDay(jmc.certificatesGeneratedAt) })}</p>
								)}
								<p className={styles.hint}>
									{t('Junior Mini Companies are deleted with the annual reset of the database. Please download the certificates beforehand.')}
								</p>
								{jmc.certificatesGeneratedAt ? (
									<Btn onClick={() => downloadPdf(TApi.TEACHER_MINI_COMPANY_CERTIFICATES(id), zertifikateDatei)}>
										{t('Download certificates')}
									</Btn>
								) : (
									<Btn onClick={zertifikateErzeugen} loading={busy === 'certificates'} disabled={!kinder.length}>
										{t('Generate certificates')}
									</Btn>
								)}
							</>
						)}
					</section>
				</div>

				<aside className={styles.side}>
					<section className={styles.card}>
						<h2 className={styles.cardTitle}>{t('Teachers')}</h2>
						<p className={styles.hint}>{t('All teachers of this Junior Mini Company have the same rights.')}</p>
						<TeacherList
							teachers={jmc.teachers || []}
							ownId={ownId}
							actions={teacher =>
								(jmc.teachers || []).length > 1 ? (
									<Btn size="small" theme="minimal" onClick={() => lehrkraftAustragen(teacher)}>
										{teacher.id === ownId ? t('Leave') : t('Remove')}
									</Btn>
								) : null
							}
						/>
						<label className={styles.field}>
							{t('Your role in the class')}
							<select value={jmc.myClassRole || ''} onChange={eigeneRolle} disabled={busy === 'role'}>
								<option value="">{t('Please select')}</option>
								{optionsOf(JMC_CLASS_ROLE_LABELS, t).map(o => (
									<option key={o.value} value={o.value}>
										{o.label}
									</option>
								))}
							</select>
						</label>
						{(jmc.invitations || []).length > 0 && (
							<ul className={styles.invitations}>
								{jmc.invitations.map(inv => (
									<li key={inv.id}>
										<span>
											{inv.email} · {t('Invitation sent')}
										</span>
										<Btn size="small" theme="minimal" onClick={() => einladungZurueck(inv)}>
											{t('Withdraw invitation')}
										</Btn>
									</li>
								))}
							</ul>
						)}
						<form className={styles.invite} onSubmit={lehrkraftHinzufuegen} noValidate>
							<label className={styles.field}>
								{t('E-mail address of the teacher')}
								<input type="email" value={email} onChange={event => setEmail(event.target.value)} />
							</label>
							<span className={styles.fieldHint}>
								{t(
									'If the teacher already has an account at your school, they are added straight away. Otherwise they receive an invitation to register.'
								)}
							</span>
							<Btn type="submit" theme="secondary" loading={busy === 'teacher'} disabled={!email.trim()}>
								{t('Add teacher')}
							</Btn>
						</form>
					</section>

					<section className={styles.card}>
						<h2 className={styles.cardTitle}>{t('History')}</h2>
						<LogList items={log} />
					</section>
				</aside>
			</div>

			{vorschau && jmc.consent && (
				<FilePreview
					items={[{ name: jmc.consent.originalName, url: TApi.TEACHER_MINI_COMPANY_CONSENT(id) }]}
					index={0}
					onIndexChange={() => {}}
					onClose={() => setVorschau(false)}
					onDownload={() => downloadPdf(TApi.TEACHER_MINI_COMPANY_CONSENT(id), jmc.consent.originalName)}
				/>
			)}
		</div>
	)
}

function GenderSelect({ value, onChange }) {
	const { t } = useTranslation()
	return (
		<select aria-label={t('Gender')} value={value} onChange={onChange}>
			<option value="">{t('Please select')}</option>
			{optionsOf(JMC_GENDER_LABELS, t).map(o => (
				<option key={o.value} value={o.value}>
					{o.label}
				</option>
			))}
		</select>
	)
}

/** Kopfzeile unter dem Titel: was gerade Sache ist. */
function StandBanner({ jmc }) {
	const { t } = useTranslation()
	if (jmc.status === JMC_STATUS.REJECTED)
		return (
			<div className={`${styles.banner} ${styles.bannerRed}`}>
				<strong>{t('Returned by the state administration:')}</strong> {jmc.rejectionReason}
				<br />
				{t('Please make the changes and submit again.')}
			</div>
		)
	if (jmc.status === JMC_STATUS.ACTIVE)
		return (
			<div className={`${styles.banner} ${styles.bannerGreen}`}>
				{t('Approved on {{date}}. The Junior Mini Company is active.', { date: formatDay(jmc.decidedAt) })}
			</div>
		)
	if (jmc.status === JMC_STATUS.SUBMITTED)
		return (
			<div className={`${styles.banner} ${styles.bannerOrange}`}>
				{t(
					'Submitted on {{date}}. The state administration is checking the Junior Mini Company — until then nothing can be changed.',
					{ date: formatDay(jmc.submittedAt) }
				)}
			</div>
		)
	return null
}

const mapStateToProps = state => ({ ownId: state.user.profile.id })

export default connect(mapStateToProps)(TeacherMiniCompany)
